import React, { useContext, useRef, useState } from 'react';
import { Context } from 'components/Store';
import PropTypes from 'prop-types';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import PropertyLandingContext from 'screens/Property/PropertiesLanding/context';
import usePropertyFilters from 'hooks/usePropertyFilters';

const PropertyBreadcrumb = ({
  disabledOrgChange = false,
  disabledPropertyChange = false
}) => {
  const [context] = useContext(Context);
  const { selectedProperty } = context;
  const {
    isLoadingProperties,
    setParams,
    onPropertySelection,
    isFarmSelected,
    isFieldSelected,
    isHugeOrganization,
    guardNavigation,
    isFormDirty
  } = useContext(PropertyLandingContext);
  const [{ isEnableInfoTabEdit }] = useContext(Context);
  const { filteredProperties } = usePropertyFilters(
    undefined,
    isHugeOrganization
  );

  const getBreadcrumbTitle = () => {
    if (isEnableInfoTabEdit) {
      if (isFarmSelected) {
        return 'Edit Farm';
      }
      if (isFieldSelected) {
        return `Edit Field info: ${selectedProperty?.name}`;
      }
    }
    if (!selectedProperty?.id) {
      return 'All Properties';
    }
    if (isFarmSelected) {
      return selectedProperty?.name;
    }
    return (
      filteredProperties.find(farm => farm.id === selectedProperty.farmId)
        ?.name || selectedProperty?.name
    );
  };

  const title = getBreadcrumbTitle();

  const handleOrganizationSelect = org => {
    onPropertySelection();
    setParams({ page: 0, orgId: org?.id });
  };

  // Crop season and organization switches otherwise dispatch into global state
  // immediately, before the unsaved-changes guard ever runs — deferring the
  // actual switch (`selectFunction`) behind guardNavigation makes the modal
  // block the switch itself, not just react to it afterward.
  const previousSelectedCropSeasonsRef = useRef(null);

  // BreadcrumbCropSeasons persists the new selection to localStorage itself
  // before calling back at all, so the "previous" value has to be captured
  // here — the one hook that runs before that write, not in onCropSeasonConfirm.
  const handleCropSeasonValidation = () => {
    previousSelectedCropSeasonsRef.current = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );
    return true;
  };

  // Remounting just the crop season dropdown (via Breadcrumb's
  // cropSeasonResetKey, rather than redispatching the global `cropSeasons`
  // list or remounting Breadcrumb itself) forces it to re-read the reverted
  // localStorage value. Earlier versions of this fix redispatched `cropSeasons`
  // globally, which rippled into unrelated property/map data hooks, and then
  // remounted the whole Breadcrumb, which re-ran its own mount-time crop
  // season fetch and cleared the open edit form's property selection — both
  // silently reset the form's dirty tracking.
  const [cropSeasonResetKey, setCropSeasonResetKey] = useState(0);

  const handleCropSeasonConfirm = (seasons, selectFunction) => {
    guardNavigation(
      async () => {
        await selectFunction();
        handleOrganizationSelect();
      },
      () => {
        localStorage.setItem(
          'selectedCropSeasons',
          JSON.stringify(previousSelectedCropSeasonsRef.current)
        );
        setCropSeasonResetKey(key => key + 1);
      }
    );
  };

  const handleOrganizationConfirm = (org, selectFunction) => {
    guardNavigation(selectFunction);
  };

  // Creating a new crop season/org otherwise lets the user fill out and
  // submit the whole create form before the guard ever runs, so by the time
  // it shows up the season/org already exists in the backend. Gating the
  // create form's own open blocks that earlier, the same way selecting an
  // existing season/org is blocked before it switches anything.
  //
  // Only routed through guardNavigation when the form is actually dirty:
  // guardNavigation's clean-form branch also exits edit mode, which is right
  // for a real navigation but not for merely opening a dialog the user might
  // immediately cancel without ever leaving the current crop zone. Shared by
  // both the season and organization create flows, which gate identically.
  const handleCreateConfirm = openCreateModal => {
    if (isFormDirty) {
      guardNavigation(openCreateModal);
    } else {
      openCreateModal();
    }
  };

  return (
    <Breadcrumb
      cropSeasonResetKey={cropSeasonResetKey}
      onOrganizationSelect={handleOrganizationSelect}
      onOrganizationConfirm={handleOrganizationConfirm}
      onOrganizationCreateConfirm={handleCreateConfirm}
      disabled={disabledOrgChange}
      disabledCropSeasons={isLoadingProperties}
      hideCropSeasonDropdown={false}
      onCropSeasonSelect={handleOrganizationSelect}
      onCropSeasonConfirm={handleCropSeasonConfirm}
      onCropSeasonCreateConfirm={handleCreateConfirm}
      onCropSeasonValidation={handleCropSeasonValidation}
    >
      {disabledPropertyChange && (
        <Breadcrumb.Item title="Properties" value={title} isLast />
      )}
      {!disabledPropertyChange && title && (
        <Breadcrumb.Item title="Properties" value={title} />
      )}
    </Breadcrumb>
  );
};

PropertyBreadcrumb.propTypes = {
  disabledOrgChange: PropTypes.bool,
  disabledPropertyChange: PropTypes.bool
};

export default PropertyBreadcrumb;
